/**
 * IST-aware date helpers for dashboard filters.
 *
 * WHY THIS EXISTS
 * The business operates in Asia/Kolkata (IST, UTC+5:30, no DST). The backend's
 * dashboard endpoints (analytics.py, marketing.py) interpret YYYY-MM-DD query
 * params as IST wall-clock dates and convert them to UTC boundaries server-side
 * (see routers/marketing.py:43-55 for the pattern).
 *
 * The frontend MUST therefore send date strings that reflect IST wall-clock —
 * NOT the browser's local timezone and NOT UTC.
 *
 * The classic bug is `date.toISOString().split('T')[0]` — this always returns
 * the UTC date component. When IST wall-clock is "2026-04-01 04:00 IST", UTC
 * is "2026-03-31 22:30 UTC" → toISOString returns "2026-03-31" → backend pulls
 * in an extra day of data from the previous month.
 *
 * All dashboard MTD / Today / Last-7-days / Last-30-days computations across
 * the frontend must use THESE helpers instead.
 */

const IST_PARTS_FMT = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Kolkata',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/** Get IST year/month/day components for a JS Date (defaults to now). */
function istParts(date = new Date()) {
  const parts = IST_PARTS_FMT.formatToParts(date);
  return Object.fromEntries(parts.map((p) => [p.type, p.value]));
}

/** Format a JS Date as "YYYY-MM-DD" in IST wall-clock. */
export function formatISTDate(date = new Date()) {
  const { year, month, day } = istParts(date);
  return `${year}-${month}-${day}`;
}

/** Today in IST as "YYYY-MM-DD". */
export const todayIST = () => formatISTDate(new Date());

/**
 * N days ago in IST as "YYYY-MM-DD".
 * Anchor at IST midnight (`T00:00:00+05:30`) to avoid DST/browser-TZ drift —
 * IST has no DST so this is stable year-round.
 */
export function daysAgoIST(n) {
  const anchor = new Date(`${todayIST()}T00:00:00+05:30`);
  anchor.setDate(anchor.getDate() - n);
  return formatISTDate(anchor);
}

/** First day of current IST month as "YYYY-MM-DD". */
export function monthStartIST() {
  const { year, month } = istParts(new Date());
  return `${year}-${month}-01`;
}

/** First day of current IST year as "YYYY-MM-DD" (for YTD filters). */
export function yearStartIST() {
  const { year } = istParts(new Date());
  return `${year}-01-01`;
}

/**
 * Compute {start_date, end_date} for a named filter preset. Returns IST-correct
 * YYYY-MM-DD strings that the backend interprets as IST wall-clock dates.
 *
 * Supported presets: all | today | yesterday | tomorrow | last7days |
 * last30days | mtd | ytd | custom. For 'custom' returns nulls so the caller's
 * manually-picked dates are preserved.
 */
export function istRangeForPreset(preset) {
  switch (preset) {
    case 'today':
      return { start_date: todayIST(), end_date: todayIST() };
    case 'yesterday':
      return { start_date: daysAgoIST(1), end_date: daysAgoIST(1) };
    case 'tomorrow':
      return { start_date: daysAgoIST(-1), end_date: daysAgoIST(-1) };
    case 'last7days':
      return { start_date: daysAgoIST(7), end_date: todayIST() };
    case 'last30days':
      return { start_date: daysAgoIST(30), end_date: todayIST() };
    case 'last90days':
      return { start_date: daysAgoIST(90), end_date: todayIST() };
    case 'mtd':
      return { start_date: monthStartIST(), end_date: todayIST() };
    case 'ytd':
      return { start_date: yearStartIST(), end_date: todayIST() };
    case 'all':
      return { start_date: '', end_date: '' };
    case 'custom':
    default:
      return null; // caller keeps existing manually-picked dates
  }
}
